import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, User, Mail, Phone, Tag, Calendar, ShoppingBag, 
  ChevronRight, X, RefreshCw, Crown, 
  Newspaper, UserCheck, Gift, DollarSign, History, Filter,
  BarChart3, TrendingUp, Target, Shield, Award, Star, Coffee
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Tier color mapping
const TIER_COLORS = {
  bronze: { bg: 'bg-amber-700', text: 'text-white', hex: '#cd7f32' },
  silver: { bg: 'bg-gray-400', text: 'text-gray-900', hex: '#c0c0c0' },
  gold: { bg: 'bg-yellow-500', text: 'text-yellow-900', hex: '#ffd700' },
  platinum: { bg: 'bg-gray-300', text: 'text-gray-800', hex: '#e5e4e2' }
};

export default function ContactsTab({ authHeaders, currentAdmin }) {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [rfmStats, setRfmStats] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [tierStatus, setTierStatus] = useState(null);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [migrating, setMigrating] = useState(false);
  const [analyzingRfm, setAnalyzingRfm] = useState(false);
  const [showRfmPanel, setShowRfmPanel] = useState(false);
  const [settingTier, setSettingTier] = useState(false);
  const [tierOverrideReason, setTierOverrideReason] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Check if current admin is an owner
  const isOwner = currentAdmin?.role?.toLowerCase() === 'owner' || currentAdmin?.is_super_admin;

  const fetchContacts = useCallback(async () => {
    try {
      let url = `/crm/contacts?page=${page}&limit=50&sort=${sortField}&order=${sortOrder}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (tagFilter) url += `&tag=${encodeURIComponent(tagFilter)}`;
      
      // Status filters
      if (statusFilter === 'accounts') url += '&has_account=true';
      if (statusFilter === 'newsletter') url += '&newsletter=true';
      if (statusFilter === 'loyalty') url += '&loyalty=true';
      if (statusFilter === 'customers') url += '&has_orders=true';
      
      const res = await api.get(url);
      setContacts(res.data.contacts || []);
      setTotalPages(res.data.pages || 1);
      setTotalContacts(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      toast.error('Failed to load contacts');
    }
    setLoading(false);
  }, [page, search, tagFilter, statusFilter, sortField, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/crm/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchRfmStats = useCallback(async () => {
    try {
      const res = await api.get('/crm/rfm-stats');
      setRfmStats(res.data);
    } catch (err) {
      console.error('Failed to fetch RFM stats:', err);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const res = await api.get('/crm/tags');
      setAllTags(res.data || []);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  }, []);

  const fetchContactDetails = async (contactId) => {
    try {
      const res = await api.get(`/crm/contacts/${contactId}`);
      setSelectedContact(res.data);
      
      // Also fetch tier status
      const tierRes = await api.get(`/crm/contacts/${contactId}/tier-status`);
      setTierStatus(tierRes.data);
    } catch (err) {
      console.error('Failed to fetch contact details:', err);
      toast.error('Failed to load contact details');
    }
  };

  const fetchContactsBySegment = async (segment) => {
    setLoading(true);
    try {
      const res = await api.get(`/crm/contacts/segment/${segment}?page=${page}&limit=50`);
      setContacts(res.data.contacts || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch segment contacts:', err);
      toast.error('Failed to load segment contacts');
    }
    setLoading(false);
  };

  const runMigration = async () => {
    setMigrating(true);
    try {
      const res = await api.post('/crm/migrate-all');
      
      if (res.data.success) {
        toast.success(res.data.message);
        fetchContacts();
        fetchStats();
      } else {
        toast.error('Migration failed');
      }
    } catch (err) {
      toast.error('Migration request failed');
    }
    setMigrating(false);
  };

  // Fix loyalty signups that don't have corresponding contacts
  const [fixingLoyalty, setFixingLoyalty] = useState(false);
  const [loyaltyMismatch, setLoyaltyMismatch] = useState(null);
  
  // Check for loyalty/contact mismatch on load
  const checkLoyaltyMismatch = useCallback(async () => {
    try {
      const res = await api.get('/admin/loyalty-contact-check');
      if (res.data && res.data.missing_contacts > 0) {
        setLoyaltyMismatch(res.data);
      } else {
        setLoyaltyMismatch(null);
      }
    } catch (err) {
      console.error('Failed to check loyalty mismatch:', err);
    }
  }, []);
  
  const fixLoyaltySignups = async () => {
    setFixingLoyalty(true);
    try {
      const res = await api.post('/admin/fix-missing-loyalty-contacts');
      
      if (res.data.success) {
        const { contacts_created, already_had_contacts, total_loyalty_signups } = res.data;
        if (contacts_created > 0) {
          toast.success(`Fixed ${contacts_created} loyalty signups! ${already_had_contacts} already had contacts.`);
        } else {
          toast.info(`All ${total_loyalty_signups} loyalty signups already have contacts.`);
        }
        setLoyaltyMismatch(null);
        fetchContacts();
        fetchStats();
      } else {
        toast.error('Failed to fix loyalty signups');
      }
    } catch (err) {
      console.error('Fix loyalty signups error:', err);
      toast.error('Failed to fix loyalty signups');
    }
    setFixingLoyalty(false);
  };

  const runDeduplication = async () => {
    try {
      const res = await api.post('/crm/deduplicate');
      
      if (res.data.success) {
        toast.success(`Merged ${res.data.contacts_merged} duplicate groups, deleted ${res.data.contacts_deleted} duplicates`);
        fetchContacts();
        fetchStats();
      }
    } catch (err) {
      toast.error('Deduplication failed');
    }
  };

  const runRfmAnalysis = async () => {
    setAnalyzingRfm(true);
    try {
      const res = await api.post('/crm/rfm-analysis');
      
      if (res.data.success) {
        toast.success(`RFM Analysis complete! Analyzed ${res.data.contacts_analyzed} customers`);
        fetchContacts();
        fetchRfmStats();
      } else {
        toast.error('RFM analysis failed');
      }
    } catch (err) {
      toast.error('RFM analysis request failed');
    }
    setAnalyzingRfm(false);
  };

  const setManualTierOverride = async (tier) => {
    if (!selectedContact || !isOwner) return;
    
    setSettingTier(true);
    try {
      const staffId = currentAdmin?.id || currentAdmin?.staff_id || 'admin';
      const staffRole = currentAdmin?.role || 'owner';
      
      const res = await api.post(
        `/crm/contacts/${selectedContact.contact_id}/tier-override?staff_id=${staffId}&staff_role=${staffRole}`,
        {
          tier: tier,
          reason: tierOverrideReason || null
        }
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        // Refresh contact details
        await fetchContactDetails(selectedContact.contact_id);
        setTierOverrideReason('');
      } else {
        toast.error(res.data.detail || 'Failed to set tier override');
      }
    } catch (err) {
      console.error('Tier override error:', err);
      toast.error(err.response?.data?.detail || 'Failed to set tier override');
    }
    setSettingTier(false);
  };

  useEffect(() => {
    if (segmentFilter) {
      fetchContactsBySegment(segmentFilter);
    } else {
      fetchContacts();
    }
    fetchStats();
    fetchRfmStats();
    fetchTags();
    checkLoyaltyMismatch(); // Check for missing loyalty contacts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchContacts, fetchStats, fetchRfmStats, fetchTags, segmentFilter, page, checkLoyaltyMismatch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  // Status badge component
  const StatusBadge = ({ label, color, icon: Icon }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${color}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </span>
  );

  // RFM Segment badge component
  const RfmBadge = ({ segment, segmentName, color }) => (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full text-white font-medium"
      style={{ backgroundColor: color || '#6b7280' }}
    >
      <Target className="w-3 h-3" />
      {segmentName || segment}
    </span>
  );

  // Tier badge component
  const TierBadge = ({ tier, isManual }) => {
    if (!tier) return null;
    const tierLower = tier.toLowerCase();
    const colors = TIER_COLORS[tierLower] || { bg: 'bg-gray-500', text: 'text-white' };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium ${colors.bg} ${colors.text}`}>
        {tierLower === 'platinum' ? <Crown className="w-3 h-3" /> : <Award className="w-3 h-3" />}
        {tier}
        {isManual && <Star className="w-3 h-3 fill-current" />}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="contacts-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl text-foreground">Master CRM</h1>
          <p className="text-muted-foreground">Unified view of all customers, accounts, and subscribers</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRfmPanel(!showRfmPanel)}
            className="text-sm"
            data-testid="rfm-panel-btn"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            RFM Segments
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={runRfmAnalysis}
            disabled={analyzingRfm}
            className="text-sm bg-primary/10 hover:bg-primary/20 text-primary"
            data-testid="rfm-analyze-btn"
          >
            <TrendingUp className={`w-4 h-4 mr-2 ${analyzingRfm ? 'animate-pulse' : ''}`} />
            {analyzingRfm ? 'Analyzing...' : 'Run RFM Analysis'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={runDeduplication}
            className="text-sm"
            data-testid="dedupe-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Deduplicate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={runMigration}
            disabled={migrating}
            className="text-sm"
            data-testid="migrate-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${migrating ? 'animate-spin' : ''}`} />
            {migrating ? 'Migrating...' : 'Run Migration'}
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={fixLoyaltySignups}
            disabled={fixingLoyalty}
            className="text-base font-bold bg-red-500 hover:bg-red-600 text-white border-red-600 shadow-lg animate-pulse"
            data-testid="fix-loyalty-btn"
          >
            <Coffee className={`w-5 h-5 mr-2 ${fixingLoyalty ? 'animate-spin' : ''}`} />
            {fixingLoyalty ? 'Fixing...' : '⚠️ Fix Missing Loyalty Contacts'}
          </Button>
        </div>
      </div>

      {/* ALERT: Missing Loyalty Contacts */}
      {loyaltyMismatch && loyaltyMismatch.missing_contacts > 0 && (
        <div className="bg-red-500 text-white rounded-xl p-4 mb-4 shadow-lg border-2 border-red-600" data-testid="loyalty-mismatch-alert">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Coffee className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">⚠️ {loyaltyMismatch.missing_contacts} Loyalty Members Missing from Contacts!</h3>
                <p className="text-red-100 text-sm">
                  These members signed up for loyalty but their contact records weren't created. Click the button to fix this now.
                </p>
              </div>
            </div>
            <Button
              onClick={fixLoyaltySignups}
              disabled={fixingLoyalty}
              className="bg-white text-red-600 hover:bg-red-50 font-bold px-6 py-3"
              data-testid="fix-loyalty-alert-btn"
            >
              <RefreshCw className={`w-5 h-5 mr-2 ${fixingLoyalty ? 'animate-spin' : ''}`} />
              {fixingLoyalty ? 'Fixing...' : 'Fix Now'}
            </Button>
          </div>
        </div>
      )}

      {/* RFM Segments Panel */}
      {showRfmPanel && rfmStats && (
        <div className="bg-card rounded-xl p-6 border border-border" data-testid="rfm-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg text-foreground flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Customer Segmentation (RFM Analysis)
            </h2>
            <div className="text-sm text-muted-foreground">
              {rfmStats.total_analyzed} customers analyzed
              {rfmStats.pending_analysis > 0 && (
                <span className="ml-2 text-amber-500">({rfmStats.pending_analysis} pending)</span>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {rfmStats.segments?.map(seg => (
              <button
                key={seg.segment}
                onClick={() => {
                  setSegmentFilter(seg.segment);
                  setStatusFilter('');
                  setTagFilter('');
                  setPage(1);
                }}
                className={`p-3 rounded-lg border transition-all hover:scale-105 ${
                  segmentFilter === seg.segment 
                    ? 'ring-2 ring-primary shadow-lg' 
                    : 'hover:shadow-md'
                }`}
                style={{ borderColor: seg.color + '40', backgroundColor: seg.color + '10' }}
                data-testid={`rfm-segment-${seg.segment}`}
              >
                <div 
                  className="w-3 h-3 rounded-full mb-2"
                  style={{ backgroundColor: seg.color }}
                />
                <div className="text-sm font-medium text-foreground">{seg.name}</div>
                <div className="text-2xl font-bold" style={{ color: seg.color }}>{seg.count}</div>
                <div className="text-xs text-muted-foreground">
                  {formatCurrency(seg.avg_spent)} avg
                </div>
              </button>
            ))}
          </div>
          
          {segmentFilter && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filtering by:</span>
              <RfmBadge 
                segment={segmentFilter} 
                segmentName={rfmStats.segments?.find(s => s.segment === segmentFilter)?.name}
                color={rfmStats.segments?.find(s => s.segment === segmentFilter)?.color}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSegmentFilter(''); fetchContacts(); }}
                className="text-xs"
              >
                Clear filter
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-primary">{stats.total_contacts}</div>
            <div className="text-sm text-muted-foreground">Total Contacts</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-blue-500">{stats.with_account}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1" title="Contacts with rewards/login account">
              <UserCheck className="w-3 h-3" /> Rewards Accounts
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-green-500">{stats.customers_with_orders}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <ShoppingBag className="w-3 h-3" /> Customers
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-amber-500">{stats.loyalty_members}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <Crown className="w-3 h-3" /> Loyalty
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-teal-500">{stats.newsletter_subscribers}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <Newspaper className="w-3 h-3" /> Newsletter
            </div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-violet-500">{formatCurrency(stats.total_revenue)}</div>
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Revenue
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-xl p-4 border border-border">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); setSegmentFilter(''); }}
                className="pl-10"
                data-testid="contact-search"
              />
            </div>
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); setSegmentFilter(''); }}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="status-filter"
            >
              <option value="">All Contacts</option>
              <option value="accounts">With Account</option>
              <option value="customers">With Orders</option>
              <option value="loyalty">Loyalty Members</option>
              <option value="newsletter">Newsletter</option>
            </select>
          </div>
          <div>
            <select
              value={tagFilter}
              onChange={(e) => { setTagFilter(e.target.value); setPage(1); setSegmentFilter(''); }}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="tag-filter"
            >
              <option value="">All Tags</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contact</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Phone</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Source</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Orders</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total Spent</th>
                <th 
                  className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none"
                  onClick={() => {
                    if (sortField === 'created_at') {
                      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                    } else {
                      setSortField('created_at');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  <span className="flex items-center gap-1">
                    Date Created
                    {sortField === 'created_at' && (
                      <span className="text-primary">{sortOrder === 'desc' ? '↓' : '↑'}</span>
                    )}
                  </span>
                </th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted-foreground">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                    Loading contacts...
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-muted-foreground">
                    {search || tagFilter || statusFilter || segmentFilter ? (
                      <div>
                        <p>No contacts match your filters.</p>
                        <Button 
                          variant="link" 
                          onClick={() => { setSearch(''); setTagFilter(''); setStatusFilter(''); setSegmentFilter(''); }}
                        >
                          Clear filters
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <p className="mb-2">No contacts found.</p>
                        <Button onClick={runMigration} disabled={migrating}>
                          <RefreshCw className={`w-4 h-4 mr-2 ${migrating ? 'animate-spin' : ''}`} />
                          Run Migration to Import Data
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                contacts.map(contact => (
                  <tr 
                    key={contact.contact_id} 
                    className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => fetchContactDetails(contact.contact_id)}
                    data-testid={`contact-row-${contact.contact_id}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium text-foreground flex items-center gap-2">
                            {contact.name || 'Unknown'}
                            {contact.is_founding_member && (
                              <Crown className="w-4 h-4 text-amber-500" title="Founding Member" />
                            )}
                            {contact.manual_tier_override && (
                              <Star className="w-4 h-4 text-primary" title="Manual Tier Override" />
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">{contact.email || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground font-mono">
                      {contact.phone || '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {contact.has_account && (
                          <StatusBadge label="Account" color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" icon={UserCheck} />
                        )}
                        {contact.loyalty_member && (
                          <StatusBadge label="Loyalty" color="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" icon={Crown} />
                        )}
                        {contact.newsletter_subscribed && (
                          <StatusBadge label="Newsletter" color="bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300" icon={Newspaper} />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {(contact.source_type || contact.sources?.[0]?.type) ? (
                        <span className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium capitalize">
                          {(contact.source_type || contact.sources?.[0]?.type || '').replace(/_/g, ' ')}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {contact.order_count > 0 ? (
                        <span className="font-medium text-foreground">{contact.order_count}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {contact.total_spent > 0 ? (
                        <span className="font-medium text-green-600 dark:text-green-400">
                          {formatCurrency(contact.total_spent)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {formatDate(contact.created_at)}
                    </td>
                    <td className="py-3 px-4">
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-border">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Page</span>
              <select
                value={page}
                onChange={(e) => setPage(Number(e.target.value))}
                className="bg-background border border-border rounded-md px-2 py-1 text-sm text-foreground"
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <span className="text-sm text-muted-foreground">of {totalPages}</span>
              <span className="text-sm text-muted-foreground ml-2">({totalContacts} total)</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages}
              >
                Last
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Contact Detail Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                    {selectedContact.name || 'Unknown Contact'}
                    {selectedContact.is_founding_member && (
                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Founding Member
                      </span>
                    )}
                  </h2>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-1">
                    {selectedContact.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {selectedContact.email}
                      </span>
                    )}
                    {selectedContact.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" /> {selectedContact.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setSelectedContact(null); setTierStatus(null); setTierOverrideReason(''); }}
                className="p-2 hover:bg-muted rounded-full transition-colors"
                data-testid="close-contact-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Loyalty Tier Status Card */}
              {tierStatus && (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 rounded-xl p-5 border border-amber-200 dark:border-amber-800">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-lg text-foreground flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-500" />
                      Loyalty Tier Status
                    </h3>
                    {tierStatus.has_manual_override && (
                      <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> Owner Override Active
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground mb-1">Effective Tier</div>
                      <TierBadge tier={tierStatus.effective_tier_name} isManual={tierStatus.has_manual_override} />
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground mb-1">Discount</div>
                      <div className="text-2xl font-bold text-green-600">{tierStatus.effective_discount}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground mb-1">Total Spent</div>
                      <div className="text-lg font-bold text-foreground">{formatCurrency(tierStatus.total_spent)}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground mb-1">Next Tier</div>
                      {tierStatus.next_tier ? (
                        <div className="text-sm">
                          <span className="font-medium">{tierStatus.next_tier_name}</span>
                          <div className="text-xs text-muted-foreground">{formatCurrency(tierStatus.amount_to_next_tier)} away</div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Max tier</span>
                      )}
                    </div>
                  </div>

                  {/* Platinum Perks */}
                  {tierStatus.is_platinum && (
                    <div className="mt-4 p-3 bg-white/50 dark:bg-black/20 rounded-lg">
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                        <Coffee className="w-4 h-4 text-amber-600" />
                        Platinum Perks
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Eligible for daily free coffee when visiting in-store
                      </div>
                    </div>
                  )}
                  
                  {/* Manual Override Info */}
                  {tierStatus.has_manual_override && (
                    <div className="mt-4 p-3 bg-primary/10 rounded-lg">
                      <div className="text-sm">
                        <span className="font-medium">Manual Override:</span> {tierStatus.manual_tier_name}
                        {tierStatus.earned_tier && tierStatus.earned_discount > tierStatus.manual_discount && (
                          <span className="text-xs text-muted-foreground ml-2">
                            (Using earned {tierStatus.earned_tier_name} tier - higher discount)
                          </span>
                        )}
                      </div>
                      {tierStatus.manual_set_at && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Set on {formatDate(tierStatus.manual_set_at)}
                          {tierStatus.manual_reason && <span> - {tierStatus.manual_reason}</span>}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Owner-Only: Manual Tier Override Controls */}
                  {isOwner && (
                    <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-800">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-foreground">Owner: Set Manual Tier Override</span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {['bronze', 'silver', 'gold', 'platinum'].map(tier => {
                          const colors = TIER_COLORS[tier];
                          const isCurrentManual = tierStatus.has_manual_override && tierStatus.manual_tier === tier;
                          return (
                            <button
                              key={tier}
                              onClick={() => setManualTierOverride(tier)}
                              disabled={settingTier}
                              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                isCurrentManual 
                                  ? 'ring-2 ring-primary ring-offset-2' 
                                  : 'hover:scale-105'
                              } ${colors.bg} ${colors.text}`}
                              data-testid={`set-tier-${tier}`}
                            >
                              {tier.charAt(0).toUpperCase() + tier.slice(1)}
                              {tier === 'platinum' && <Crown className="w-3 h-3 inline ml-1" />}
                            </button>
                          );
                        })}
                        {tierStatus.has_manual_override && (
                          <button
                            onClick={() => setManualTierOverride('remove')}
                            disabled={settingTier}
                            className="px-4 py-2 rounded-lg font-medium text-sm bg-red-100 text-red-700 hover:bg-red-200 transition-all"
                            data-testid="remove-tier-override"
                          >
                            Remove Override
                          </button>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Input
                          placeholder="Reason for override (optional)"
                          value={tierOverrideReason}
                          onChange={(e) => setTierOverrideReason(e.target.value)}
                          className="flex-1 text-sm"
                        />
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Customer receives the HIGHER of manual or earned tier discount. Applies to Online, Kiosk, and POS orders.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* RFM Segment (if analyzed) */}
              {selectedContact.rfm_segment && (
                <div className="bg-muted/50 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: selectedContact.rfm_segment_color + '20' }}
                      >
                        <Target className="w-6 h-6" style={{ color: selectedContact.rfm_segment_color }} />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">RFM Segment</div>
                        <div className="text-lg font-bold" style={{ color: selectedContact.rfm_segment_color }}>
                          {selectedContact.rfm_segment_name}
                        </div>
                      </div>
                    </div>
                    {selectedContact.rfm_scores && (
                      <div className="flex gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-foreground">{selectedContact.rfm_scores.recency}</div>
                          <div className="text-xs text-muted-foreground">Recency</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-foreground">{selectedContact.rfm_scores.frequency}</div>
                          <div className="text-xs text-muted-foreground">Frequency</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-foreground">{selectedContact.rfm_scores.monetary}</div>
                          <div className="text-xs text-muted-foreground">Monetary</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status Badges */}
              <div className="flex flex-wrap gap-2">
                {selectedContact.has_account && (
                  <StatusBadge label="Has Account" color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" icon={UserCheck} />
                )}
                {selectedContact.loyalty_member && (
                  <StatusBadge 
                    label={`Loyalty: ${selectedContact.loyalty_tier || 'Member'}`} 
                    color="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" 
                    icon={Crown} 
                  />
                )}
                {selectedContact.newsletter_subscribed && (
                  <StatusBadge label="Newsletter Subscriber" color="bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300" icon={Newspaper} />
                )}
                {selectedContact.order_count > 0 && (
                  <StatusBadge label="Customer" color="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" icon={ShoppingBag} />
                )}
              </div>

              {/* Tags */}
              {selectedContact.tags && selectedContact.tags.length > 0 && (
                <div>
                  <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Tag className="w-4 h-4" /> Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedContact.tags.filter(t => !t.startsWith('RFM:')).map(tag => (
                      <span 
                        key={tag} 
                        className="px-3 py-1 text-sm rounded-full bg-primary/10 text-primary"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-foreground">{selectedContact.order_count || 0}</div>
                  <div className="text-sm text-muted-foreground">Orders</div>
                </div>
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {formatCurrency(selectedContact.total_spent)}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Spent</div>
                </div>
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-amber-500">
                    {selectedContact.loyalty_points || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Loyalty Points</div>
                </div>
                <div className="bg-muted/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {(selectedContact.campaigns || []).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Campaigns</div>
                </div>
              </div>

              {/* Campaigns */}
              {selectedContact.campaigns && selectedContact.campaigns.length > 0 && (
                <div>
                  <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4" /> Campaign Signups
                  </h3>
                  <div className="space-y-2">
                    {selectedContact.campaigns.map((campaign, i) => (
                      <div key={i} className="bg-muted/50 rounded-xl p-4 flex items-center justify-between">
                        <div>
                          <div className="font-medium text-foreground">
                            {campaign.campaign_name || `Campaign ${campaign.campaign_id}`}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Signed up: {formatDate(campaign.signup_date)}
                          </div>
                        </div>
                        {campaign.coupon_code && (
                          <div className="text-right">
                            <div className="font-mono text-primary font-medium">{campaign.coupon_code}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity Timeline */}
              {selectedContact.interactions && selectedContact.interactions.length > 0 && (
                <div>
                  <h3 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <History className="w-4 h-4" /> Recent Activity
                  </h3>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {selectedContact.interactions
                      .slice()
                      .reverse()
                      .slice(0, 10)
                      .map((interaction, i) => (
                        <div key={i} className="flex items-start gap-3 text-sm">
                          <div className="w-2 h-2 mt-1.5 rounded-full bg-primary flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-foreground capitalize">
                              {interaction.type?.replace(/_/g, ' ') || 'Activity'}
                            </div>
                            <div className="text-muted-foreground">
                              {formatDate(interaction.timestamp)}
                              {interaction.order_total && (
                                <span className="ml-2 text-green-600">{formatCurrency(interaction.order_total)}</span>
                              )}
                              {interaction.tier && (
                                <span className="ml-2">Tier: {interaction.tier}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="pt-4 border-t border-border text-sm text-muted-foreground grid grid-cols-2 gap-2">
                <div>First Contact: {formatDate(selectedContact.created_at)}</div>
                <div>Last Activity: {formatDate(selectedContact.updated_at)}</div>
                {selectedContact.birthday && <div>Birthday: {selectedContact.birthday}</div>}
                {selectedContact.source_type && <div>Source: {selectedContact.source_type}</div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
